import type { GetSignedUrlResponseSchema } from "@microbeta/common-types";
import type React from "react";
import { useCallback, useState } from "react";
import type { z } from "zod";
import {
  getGenericSignedUploadUrl,
  uploadFileToGCS,
} from "../../../services/uploads";
import { FileDropzone } from "../FileDropZone";

// Define ConceptType directly to avoid deep type instantiation issues
type ConceptType = "areas" | "areas" | "models" | "routes" | "general";

interface DragDropUploaderProps {
  conceptType: ConceptType;
  conceptId: string; // UUID of the parent concept (e.g., area ID, area ID)
  onUploadComplete: (path: string) => void;
  acceptedFileTypes?: string; // e.g., "image/*, .glb"
  label?: string;
  className?: string;
  disabled?: boolean; // Add disabled prop
}

export const DragDropUploader: React.FC<DragDropUploaderProps> = ({
  conceptType,
  conceptId,
  onUploadComplete,
  acceptedFileTypes = "image/*",
  label = "Drag & drop files here, or click to select",
  className = "",
  disabled = false,
}) => {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      if (!acceptedFileTypes) return true;
      const types = acceptedFileTypes.split(",").map((t) => t.trim());
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      const isAccepted = types.some((type) => {
        if (type.startsWith(".")) return fileExtension === type;
        if (type.endsWith("/*")) return file.type.startsWith(type.slice(0, -1));
        return file.type === type;
      });
      // 100MB limit for all files (increased for 3D models)
      const maxSize = 100 * 1024 * 1024;
      return isAccepted && file.size <= maxSize;
    },
    [acceptedFileTypes]
  );

  const getValidationError = useCallback(
    (file: File): string | null => {
      if (!acceptedFileTypes) return null;

      const types = acceptedFileTypes.split(",").map((t) => t.trim());
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      const isAcceptedType = types.some((type) => {
        if (type.startsWith(".")) return fileExtension === type;
        if (type.endsWith("/*")) return file.type.startsWith(type.slice(0, -1));
        return file.type === type;
      });

      if (!isAcceptedType) {
        return `File type not supported. Accepted types: ${acceptedFileTypes}`;
      }

      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return `File too large (${fileSizeMB}MB). Maximum size is 100MB.`;
      }

      return null;
    },
    [acceptedFileTypes]
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file) return;
      if (!conceptId) {
        setError("Cannot upload file: Concept ID is missing.");
        return;
      }

      // Check for validation errors
      const validationError = getValidationError(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      setUploadProgress(0);
      setFileName(file.name);
      try {
        const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
        const fileDetails = {
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          extension: fileExtension,
        };

        const signedUrlResponse: z.infer<typeof GetSignedUrlResponseSchema> =
          await getGenericSignedUploadUrl({
            conceptType,
            conceptId,
            ...fileDetails,
          });

        const { signedUrl, destinationPath, bucketName, uploadMetadata } =
          signedUrlResponse;
        await uploadFileToGCS(
          signedUrl,
          file,
          fileDetails.contentType,
          (percentage: number) => {
            setUploadProgress(percentage);
          },
          uploadMetadata
        );
        const fullPath = `https://storage.googleapis.com/${bucketName}/${destinationPath}`;
        onUploadComplete(fullPath);
        setFileName(null);
        setUploadProgress(null);
      } catch (err: unknown) {
        console.error("Upload process failed:", err);
        const message =
          err instanceof Error
            ? err.message
            : "Upload failed. Please try again.";
        setError(message);
        setUploadProgress(null);
        setFileName(null);
      }
    },
    [conceptType, conceptId, onUploadComplete, getValidationError]
  );

  return (
    <FileDropzone
      onFileSelect={handleFileSelect}
      label={label}
      className={className}
      disabled={disabled}
      uploadProgress={uploadProgress}
      fileName={fileName}
      error={error}
      accept={acceptedFileTypes}
      validateFile={validateFile}
      hint={acceptedFileTypes === "image/*" ? undefined : acceptedFileTypes}
    />
  );
};
